import React, { useState } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ProductDeleteDialogBox from './ProductDeleteDialogBox';
import ProductsFormModal from './ProductsFormModal';
import { useDeleteProductHandler } from '../dataHandlers/deleteProductHandler';
import { DEFAULT_TAX_SCHEME } from '../../../constants/defaults';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { usePermissions } from '../../../hooks/usePermissions';
import { ARCHIVE_PRODUCT, UPDATE_PRODUCT } from '../../../constants/eInvoicePermissions';
import { useAccessDeniedMessage } from '../../../hooks/useAccessDeniedMessage';
import PermissionDeniedWrapper from '../../../components/PermissionDeniedWrapper';

const initialCurrentItemState = { item: null };

const buildClassifiedTaxCategory = (product = {}) => ({
  id: product?.classified_tax_category?.id ?? product?.tax_category ?? null,
  percent: product?.classified_tax_category?.percent ?? product?.tax_rate ?? 0,
  tax_exemption_reason_code: product?.classified_tax_category?.tax_exemption_reason_code ?? null,
  tax_exemption_reason: product?.classified_tax_category?.tax_exemption_reason ?? null,
  tax_scheme: product?.classified_tax_category?.tax_scheme ?? DEFAULT_TAX_SCHEME,
});

const ProductActions = ({ product }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openProductForm, setOpenProductForm] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(initialCurrentItemState);
  const [readOnly, setReadOnly] = useState(false);
  const { products: p } = useTranslatedMessages();
  const actionsT = p.actions_menu ?? {};
  const { hasEInvoicePermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();
  const canUpdateProduct = hasEInvoicePermission(UPDATE_PRODUCT);
  const canArchiveProduct = hasEInvoicePermission(ARCHIVE_PRODUCT);

  const { onDelete } = useDeleteProductHandler();

  const getTransformedProduct = (product) => {
    const sacEntry = product.additional_item_identification?.find((i) => i.scheme_id === 'SAC');
    return {
      ...product,
      item_name: product.item_name || '',
      unit_price: product.unit_price ?? '',
      quantity: product.quantity || '',
      unit_of_measure: product.unit_of_measure,
      classified_tax_category: buildClassifiedTaxCategory(product),
      commodity_code: product.commodity_classification?.commodity_code || product.commodity_code || null,
      item_classification_identifier:
        product.commodity_classification?.item_classification_code || product.item_classification_identifier || null,
      service_accounting_code: sacEntry?.id || product.service_accounting_code || null,
      nature_code: product.commodity_classification?.nature_code || product.nature_code || null,
    };
  };

  const handleClick = (event) => setAnchorEl(event.currentTarget);

  const handleClose = () => {
    setReadOnly(false);
    setAnchorEl(null);
  };

  const handleDetails = () => {
    setAnchorEl(null);
    setReadOnly(true);
    setCurrentItem(getTransformedProduct(product));
    setOpenProductForm(true);
  };

  const handleEdit = () => {
    if (!canUpdateProduct) {
      return;
    }
    setAnchorEl(null);
    setReadOnly(false);
    setCurrentItem(getTransformedProduct(product));
    setOpenProductForm(true);
  };

  const handleDeleteClick = () => {
    if (!canArchiveProduct) {
      return;
    }
    setAnchorEl(null);
    setCancelDialogOpen(true);
  };

  const handleDelete = async () => {
    setOpenProductForm(false);
    await onDelete(product);
    handleClose();
  };

  return (
    <>
      <IconButton data-testid="action-icon-button" onClick={handleClick} aria-label={actionsT.aria_more ?? 'more'}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: {
              boxShadow: '4px 4px 10px 0px rgba(0,0,0,0.2)',
            },
          },
        }}
      >
        <MenuItem data-testid="details" onClick={handleDetails}>
          {actionsT.details ?? 'Details'}
        </MenuItem>
        <PermissionDeniedWrapper show={!canUpdateProduct} title={accessDeniedMessage} fullWidth>
          <MenuItem data-testid="edit" disabled={!canUpdateProduct} onClick={handleEdit}>
            {actionsT.edit ?? 'Edit'}
          </MenuItem>
        </PermissionDeniedWrapper>
        {canArchiveProduct && (
          <MenuItem data-testid="delete" onClick={handleDeleteClick}>
            {actionsT.delete ?? 'Delete'}
          </MenuItem>
        )}
      </Menu>

      <ProductsFormModal
        open={openProductForm}
        handleClose={() => setOpenProductForm(false)}
        initialData={currentItem}
        readOnly={readOnly}
      />

      <ProductDeleteDialogBox
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onSubmit={handleDelete}
        product={product}
      />
    </>
  );
};

export default ProductActions;
